import cn from 'classnames';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addFilters, applyFiltersAndGetRentals } from '@/actions/rentals';
import MaxWidth from '@/components/common/layout/MaxWidth';
import { ListingTile as GeneralPurposeListingTile } from '@/components/ui/GeneralPurposeListingTile';
import { EListingTileVariant, ListingTile } from '@/components/ui/ListingTile';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { getRentalsTiles } from '@/redux/selectors/listing/similarRentals';
import {
  trackListingImageViewedEvent,
  trackListingSelectedEvent,
} from '@/services/analytics/listings';
import { EListingSource } from '@/services/analytics/listings/types';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { IRentalTile } from '@/utility/mapSearchResultToTile';

import css from './SeasonalGuideSectionBannerRVRentals.module.css';
import { TSeasonalGuideSectionBannerRvRentals } from './types';

type TSeasonalGuideSectionBannerRVRentalsProps = Omit<TSeasonalGuideSectionBannerRvRentals, 'type'>;

export const SeasonalGuideSectionBannerRVRentals = ({
  title,
  filters,
}: TSeasonalGuideSectionBannerRVRentalsProps) => {
  const dispatch = useDispatch();
  const rentals = useSelector(getRentalsTiles);
  const { isAboveDesktop, isAboveTablet } = useBreakpoint();

  const superhostBadgeEnabled = useExperimentIsEnabled(OptimizelyFlags.SUPERHOST_BADGE);
  const listingTileUpdateEnabled = useExperimentIsEnabled(OptimizelyFlags.LISTING_TILE_UPDATE);

  useEffect(() => {
    dispatch(addFilters(filters));
    dispatch(applyFiltersAndGetRentals({ count: 4 }));
  }, [dispatch, filters]);

  if (!rentals?.length) return null;

  const onClickRental = (rental: IRentalTile, index: number) => {
    if (!rental?.id) return;

    trackListingSelectedEvent({
      rental,
      eventSource: EListingSource.GRID_LAYOUT,
      listingPlacement: index + 1,
    });
  };

  const onSlideChange = (rental: IRentalTile, index: number, nextIndex: number) => {
    if (!rental?.id) return;

    trackListingImageViewedEvent(rental, EListingSource.GRID_LAYOUT, index + 1, nextIndex);
  };

  const numberOfDisplayedRVs = isAboveDesktop ? 4 : 3;

  return (
    <MaxWidth className="pb-2 mx-auto overflow-auto md:pb-0">
      <div className={cn('md:text-center mb-8 md:mb-12 lg:mb-16', css.title)}>{title}</div>

      <div className={`grid grid-flow-row md:grid-cols-${numberOfDisplayedRVs} gap-x-6 gap-y-4`}>
        {rentals.slice(0, numberOfDisplayedRVs).map(
          (rental, index) =>
            (listingTileUpdateEnabled && (
              <GeneralPurposeListingTile
                key={rental.id}
                listingTile={rental}
                onSlideChange={nextIndex => onSlideChange(rental, index, nextIndex)}
                wishlistingEventData={{ index, eventSource: EListingSource.GRID_LAYOUT }}
                showGuestFavoriteTag
                showSuperhostBadge={superhostBadgeEnabled}
                target={isAboveDesktop ? '_blank' : undefined}
                onClick={() => onClickRental(rental, index)}
                dataTestId="listing-tile"
              />
            )) || (
              <ListingTile
                key={rental.id}
                rentalTile={rental}
                onSlideChange={nextIndex => onSlideChange(rental, index, nextIndex)}
                variant={
                  isAboveTablet
                    ? EListingTileVariant.Vertical
                    : EListingTileVariant.HorizontalBorderless
                }
                target={isAboveDesktop ? '_blank' : undefined}
                onClick={() => onClickRental(rental, index)}
                showSuperhost={superhostBadgeEnabled}
                wishlistingEventData={{
                  index,
                  eventSource: EListingSource.GRID_LAYOUT,
                }}
                className="mx-4 md:mx-0"
                dataTestId="listing-tile"
              />
            ),
        )}
      </div>
    </MaxWidth>
  );
};
